/**
 * Phase 3 — main-thread client for the yard physics worker.
 *
 * Transferable ArrayBuffer ping-pong; single in-flight frame; coalesced dt.
 * Exposes a YardRuntime *mirror* for render / probes / pure read helpers.
 * Main never calls world.step — all simulation lives in the worker.
 */

import type { FixedStepper, World } from 'box3d-web';
import type { YardCaps, YardRuntime, YardStructure } from './yardPhysics';
import {
  YARD_WORKER_PROTOCOL_VERSION,
  coalescePendingDt,
  enqueueYardWorkerCmd,
  freeBuffersTransferList,
  shouldApplySnapshot,
  type MainToWorkerMessage,
  type WorkerToMainMessage,
  type YardFreeBuffers,
  type YardSnapshotMeta,
  type YardWorkerCmd,
} from './yardWorkerProtocol';

// Vite worker constructor (bundled as a separate chunk + wasm asset).
import YardPhysicsWorker from './yardPhysics.worker.ts?worker';

function emptyCaps(): YardCaps {
  return {
    explode: false,
    castRayClosest: false,
    awakeBodyCount: false,
    bodyCount: false,
    setBodyType: false,
    getLinearVelocity: false,
    setAwake: false,
    readSleepStates: false,
  };
}

/** Minimal FixedStepper stand-in — only simTime is read by probes/bench. */
function makeStepperMirror(simTime = 0): FixedStepper {
  return {
    fixedDt: 1 / 60,
    simTime,
    setMaxStepsPerFrame: () => {},
    advance: () => 0,
  } as unknown as FixedStepper;
}

/**
 * Build a render/probe mirror. `world` is a poison stub — any step/create on
 * main is a contract violation and throws.
 */
export function createYardMirrorRuntime(): YardRuntime {
  const world = new Proxy(
    {},
    {
      get(_t, prop) {
        throw new Error(
          `yard worker mirror: main must not call world.${String(prop)} (worker owns Box3D)`,
        );
      },
    },
  ) as unknown as World;

  return {
    world,
    caps: emptyCaps(),
    stepper: makeStepperMirror(0),
    buffer: null as unknown as YardRuntime['buffer'],
    sleep: null as unknown as YardRuntime['sleep'],
    structure: 'cascade',
    structureVersion: 0,
    slowMo: false,
    fixedTime: 0,
    stepMs: 0,
    awakeCount: 0,
    awakeBrickCount: 0,
    substepsUsed: 4,
    maxStepsUsed: 3,
    bodyCountTotal: 0,
    brickBodies: [],
    brickColors: [],
    chainBodies: [],
    chainJoints: [],
    ballBodyId: 0,
    cannonballs: [],
    handBodyId: 0,
    dragJoint: 0,
    handPos: { x: 0, y: 0, z: 0 },
    dragTarget: { x: 0, y: 0, z: 0 },
    dragging: false,
    bodyIds: new Int32Array(0),
    brickBodyIds: new Int32Array(0),
    brickSleepStates: new Uint8Array(0),
    brickEverAwake: new Uint8Array(0),
    everAwakeCount: 0,
    brickRetired: new Uint8Array(0),
    brickRetireDwell: new Float32Array(0),
    retiredCount: 0,
    dynamicBrickCount: 0,
    transforms: new Float32Array(0),
    prevTransforms: new Float32Array(0),
    prevValid: false,
    bodyIndex: new Map(),
    idsDirty: false,
    impacts: [],
    staticBodyCount: 0,
    bodyLimit: 0,
    profileKey: '',
    chainPeakSpeed: 0,
    chainPeakDisp: 0,
    chainSampleFrames: 0,
    chainLastMaxSpeed: 0,
  };
}

function rebuildBodyIndex(mirror: YardRuntime) {
  const ids: number[] = [];
  for (let i = 0; i < mirror.brickBodies.length; i += 1) ids.push(mirror.brickBodies[i]);
  for (let i = 0; i < mirror.chainBodies.length; i += 1) ids.push(mirror.chainBodies[i]);
  if (mirror.ballBodyId) ids.push(mirror.ballBodyId);
  for (let i = 0; i < mirror.cannonballs.length; i += 1) ids.push(mirror.cannonballs[i]);
  mirror.bodyIds = Int32Array.from(ids);
  mirror.bodyIndex = new Map(ids.map((id, index) => [id, index]));
}

/** Cheap length+element scan — avoids Array.from / Map rebuild when structure is stable. */
function intIdsEqual(existing: readonly number[], next: Int32Array): boolean {
  if (existing.length !== next.length) return false;
  for (let i = 0; i < next.length; i += 1) {
    if (existing[i] !== next[i]) return false;
  }
  return true;
}

function copyIntIds(view: Int32Array): number[] {
  const out = new Array<number>(view.length);
  for (let i = 0; i < view.length; i += 1) out[i] = view[i];
  return out;
}

export interface YardWorkerClientOptions {
  /** Inject a Worker factory (tests). Default: Vite ?worker constructor. */
  createWorker?: () => Worker;
}

/**
 * Main-thread owner of the physics worker + snapshot mirror.
 */
export class YardWorkerClient {
  readonly mirror: YardRuntime = createYardMirrorRuntime();

  private worker: Worker | null = null;
  private ready = false;
  private disposed = false;
  private initRequestId = 1;
  private initResolve: ((rt: YardRuntime) => void) | null = null;
  private initReject: ((err: Error) => void) | null = null;

  /** Single in-flight frame (no unbounded mailbox). */
  private inFlight = false;
  private pendingDt = 0;
  private pendingCascadeOpt = false;
  private cmdQueue: YardWorkerCmd[] = [];
  private nextSequence = 1;
  private _latestAppliedSequence = 0;
  /**
   * stepsThisFrame from the newest applied snapshot, pending a single advance()
   * consume. Reset to 0 when returned so render-only frames do not re-report.
   */
  private pendingStepsThisFrame = 0;

  /** Ping-pong free buffers returned to the worker. */
  private freeBuffers: YardFreeBuffers = {};

  private readonly createWorker: () => Worker;

  constructor(options: YardWorkerClientOptions = {}) {
    this.createWorker = options.createWorker ?? (() => new YardPhysicsWorker());
  }

  get isReady(): boolean {
    return this.ready && !this.disposed;
  }

  /**
   * Monotonic sequence of the latest *applied* snapshot (0 = none yet).
   * Render path uses this to rewrite instance matrices once per new snapshot.
   */
  get latestAppliedSequence(): number {
    return this._latestAppliedSequence;
  }

  /** Start worker + init runtime. Resolves with the mirror once ready. */
  start(structure: YardStructure, bodyLimit: number): Promise<YardRuntime> {
    if (this.disposed) return Promise.reject(new Error('YardWorkerClient disposed'));
    if (this.worker) return Promise.reject(new Error('YardWorkerClient already started'));

    const worker = this.createWorker();
    this.worker = worker;
    worker.onmessage = (event: MessageEvent<WorkerToMainMessage>) => {
      this.onWorkerMessage(event.data);
    };
    worker.onerror = (event) => {
      const err = new Error(event.message || 'yard physics worker error');
      if (this.initReject) {
        this.initReject(err);
        this.initResolve = null;
        this.initReject = null;
      }
    };

    const requestId = this.initRequestId++;
    return new Promise<YardRuntime>((resolve, reject) => {
      this.initResolve = resolve;
      this.initReject = reject;
      const msg: MainToWorkerMessage = {
        type: 'init',
        protocolVersion: YARD_WORKER_PROTOCOL_VERSION,
        requestId,
        structure,
        bodyLimit,
      };
      worker.postMessage(msg);
    });
  }

  /**
   * Coalesce dt and post a frame when the worker is free.
   * Returns stepsThisFrame from the newest applied snapshot at most once;
   * subsequent advance() calls return 0 until another snapshot is applied.
   */
  advance(delta: number, cascadeOptimizationActive = false): number {
    if (!this.ready || this.disposed) return 0;
    this.pendingDt = coalescePendingDt(this.pendingDt, delta);
    this.pendingCascadeOpt = cascadeOptimizationActive;
    this.tryFlush();
    const steps = this.pendingStepsThisFrame;
    this.pendingStepsThisFrame = 0;
    return steps;
  }

  postCmd(cmd: YardWorkerCmd): void {
    if (this.disposed) return;
    // Queue only — flush rides the next advance() so tools share real frame dt
    // (avoids zero-dt worker frames and keeps a single in-flight mailbox).
    enqueueYardWorkerCmd(this.cmdQueue, cmd);
  }

  // --- tool / lifecycle conveniences (opaque IDs only) ---

  cascadeLaunch(): void {
    this.postCmd({ type: 'cascadeLaunch' });
  }

  spawnCannon(
    origin: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
  ): void {
    this.postCmd({ type: 'spawnCannon', origin, dir });
  }

  boom(x: number, y: number, z: number): void {
    this.postCmd({ type: 'boom', x, y, z });
  }

  beginDrag(targetBodyId: number, x: number, y: number, z: number): void {
    this.mirror.dragging = true;
    this.postCmd({ type: 'beginDrag', targetBodyId, x, y, z });
  }

  updateDrag(x: number, y: number, z: number): void {
    this.postCmd({ type: 'updateDrag', x, y, z });
  }

  endDrag(): void {
    this.mirror.dragging = false;
    this.postCmd({ type: 'endDrag' });
  }

  setStructure(structure: YardStructure): void {
    this.postCmd({ type: 'setStructure', structure });
  }

  reset(): void {
    this.postCmd({ type: 'reset' });
  }

  setSlowMo(slowMo: boolean): void {
    this.mirror.slowMo = slowMo;
    this.postCmd({ type: 'setSlowMo', slowMo });
  }

  calmWreckingBall(): void {
    this.postCmd({ type: 'calmWreckingBall' });
  }

  sleepSettledCascadeBricks(): void {
    this.postCmd({ type: 'sleepSettledCascadeBricks' });
  }

  forceSleepAllCascadeBricks(hard = false): void {
    this.postCmd({ type: 'forceSleepAllCascadeBricks', hard });
  }

  resetChainStability(): void {
    this.mirror.chainPeakSpeed = 0;
    this.mirror.chainPeakDisp = 0;
    this.mirror.chainSampleFrames = 0;
    this.mirror.chainLastMaxSpeed = 0;
    this.postCmd({ type: 'resetChainStability' });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.ready = false;
    const w = this.worker;
    this.worker = null;
    if (w) {
      try {
        w.postMessage({ type: 'dispose' } satisfies MainToWorkerMessage);
      } catch {
        // ignore
      }
      try {
        w.terminate();
      } catch {
        // ignore
      }
    }
    if (this.initReject) {
      this.initReject(new Error('YardWorkerClient disposed before ready'));
      this.initResolve = null;
      this.initReject = null;
    }
  }

  // --- internals -----------------------------------------------------------

  private tryFlush(): void {
    if (!this.worker || !this.ready || this.disposed || this.inFlight) return;
    const hasDt = this.pendingDt > 0;
    const hasCmds = this.cmdQueue.length > 0;
    if (!hasDt && !hasCmds) return;

    const dt = this.pendingDt;
    this.pendingDt = 0;
    const cascadeOptimizationActive = this.pendingCascadeOpt;
    const cmds = this.cmdQueue.splice(0, this.cmdQueue.length);
    const sequence = this.nextSequence++;
    const freeBuffers = this.takeFreeBuffers();

    this.inFlight = true;
    const msg: MainToWorkerMessage = {
      type: 'frame',
      sequence,
      dt,
      cascadeOptimizationActive,
      cmds,
      freeBuffers,
    };
    const transfer = freeBuffersTransferList(freeBuffers);
    this.worker.postMessage(msg, transfer);
  }

  private takeFreeBuffers(): YardFreeBuffers | undefined {
    const f = this.freeBuffers;
    const has =
      f.transforms ||
      f.brickSleepStates ||
      f.brickEverAwake ||
      f.brickRetired ||
      f.brickBodyIds ||
      f.chainBodyIds ||
      f.cannonballBodyIds;
    if (!has) return undefined;
    this.freeBuffers = {};
    return f;
  }

  private onWorkerMessage(msg: WorkerToMainMessage): void {
    if (!msg || this.disposed) return;

    if (msg.type === 'error') {
      const err = new Error(msg.message);
      if (this.initReject) {
        this.initReject(err);
        this.initResolve = null;
        this.initReject = null;
      } else {
        console.error('[yardWorker]', msg.message);
      }
      this.inFlight = false;
      return;
    }

    if (msg.type === 'ready') {
      const m = this.mirror;
      m.caps = { ...msg.caps };
      m.structure = msg.structure;
      m.bodyLimit = msg.bodyLimit;
      m.profileKey = msg.profileKey;
      m.brickColors = msg.brickColors.slice();
      m.ballBodyId = msg.ballBodyId;
      m.bodyCountTotal = msg.bodyCountTotal;
      m.structureVersion = msg.structureVersion;
      m.brickBodies = new Array(msg.brickCount).fill(0);
      m.chainBodies = new Array(msg.chainCount).fill(0);
      this.ready = true;
      if (this.initResolve) {
        this.initResolve(m);
        this.initResolve = null;
        this.initReject = null;
      }
      // Kick an initial zero-cmd frame so transforms populate for first paint.
      this.pendingDt = coalescePendingDt(this.pendingDt, 1 / 60);
      this.tryFlush();
      return;
    }

    if (msg.type === 'snapshot') {
      this.inFlight = false;
      if (shouldApplySnapshot(this._latestAppliedSequence, msg.meta.sequence)) {
        this.applySnapshot(msg.meta, msg.impacts, msg.buffers, msg.brickColors);
        this._latestAppliedSequence = msg.meta.sequence;
        // One-shot: advance() will return this exactly once.
        this.pendingStepsThisFrame = msg.meta.stepsThisFrame;
      } else {
        // Stale — still recycle buffers into free pool.
        this.recycleBuffers(msg.buffers);
      }
      this.tryFlush();
    }
  }

  private applySnapshot(
    meta: YardSnapshotMeta,
    impacts: YardRuntime['impacts'],
    buffers: {
      transforms: ArrayBuffer;
      brickSleepStates: ArrayBuffer;
      brickEverAwake: ArrayBuffer;
      brickRetired: ArrayBuffer;
      brickBodyIds: ArrayBuffer;
      chainBodyIds: ArrayBuffer;
      cannonballBodyIds: ArrayBuffer;
    },
    brickColors?: string[],
  ): void {
    const m = this.mirror;

    // Preserve previous transforms for airborne / retire pure reads on main.
    const nextTransforms = new Float32Array(buffers.transforms, 0, meta.transformsBytes / 4);
    if (meta.stepsThisFrame > 0 && m.prevValid && m.transforms.length === nextTransforms.length) {
      if (m.prevTransforms.length !== m.transforms.length) {
        m.prevTransforms = new Float32Array(m.transforms.length);
      }
      m.prevTransforms.set(m.transforms);
    }

    // Recycle previous main-owned buffers into the free pool (after copy-out).
    this.recycleOwned(m);

    m.transforms = nextTransforms;
    m.brickSleepStates = new Uint8Array(buffers.brickSleepStates, 0, meta.brickSleepBytes);
    m.brickEverAwake = new Uint8Array(buffers.brickEverAwake, 0, meta.brickEverAwakeBytes);
    m.brickRetired = new Uint8Array(buffers.brickRetired, 0, meta.brickRetiredBytes);
    m.brickBodyIds = new Int32Array(buffers.brickBodyIds, 0, meta.brickBodyIdsBytes / 4);
    const chainIds = new Int32Array(buffers.chainBodyIds, 0, meta.chainBodyIdsBytes / 4);
    const cannonIds = new Int32Array(buffers.cannonballBodyIds, 0, meta.cannonballBodyIdsBytes / 4);

    // Rebuild opaque ID arrays + bodyIndex only on structure/cannon churn.
    const idsChanged =
      m.ballBodyId !== meta.ballBodyId ||
      !intIdsEqual(m.brickBodies, m.brickBodyIds) ||
      !intIdsEqual(m.chainBodies, chainIds) ||
      !intIdsEqual(m.cannonballs, cannonIds);
    if (idsChanged) {
      m.brickBodies = copyIntIds(m.brickBodyIds);
      m.chainBodies = copyIntIds(chainIds);
      m.cannonballs = copyIntIds(cannonIds);
      m.ballBodyId = meta.ballBodyId;
      rebuildBodyIndex(m);
    }

    // Chain/cannon ID buffers are not retained as mirror views — recycle now so
    // every snapshot does not force a fresh worker allocation.
    if (buffers.chainBodyIds.byteLength > 0) this.freeBuffers.chainBodyIds = buffers.chainBodyIds;
    if (buffers.cannonballBodyIds.byteLength > 0) {
      this.freeBuffers.cannonballBodyIds = buffers.cannonballBodyIds;
    }

    m.stepMs = meta.stepMs;
    m.awakeCount = meta.awakeCount;
    m.awakeBrickCount = meta.awakeBrickCount;
    m.everAwakeCount = meta.everAwakeCount;
    m.retiredCount = meta.retiredCount;
    m.dynamicBrickCount = meta.dynamicBrickCount;
    m.bodyCountTotal = meta.bodyCountTotal;
    m.fixedTime = meta.fixedTime;
    m.substepsUsed = meta.substepsUsed;
    m.maxStepsUsed = meta.maxStepsUsed;
    m.structure = meta.structure;
    m.structureVersion = meta.structureVersion;
    m.slowMo = meta.slowMo;
    m.prevValid = meta.prevValid || (meta.stepsThisFrame > 0 && m.transforms.length > 0);
    m.profileKey = meta.profileKey;
    m.chainPeakSpeed = meta.chainPeakSpeed;
    m.chainPeakDisp = meta.chainPeakDisp;
    m.chainSampleFrames = meta.chainSampleFrames;
    m.chainLastMaxSpeed = meta.chainLastMaxSpeed;
    m.stepper = makeStepperMirror(meta.simTime);

    if (brickColors) {
      m.brickColors = brickColors.slice();
    }

    // Impacts for FX — append for the page to drain (same as main-thread path).
    for (let i = 0; i < impacts.length; i += 1) {
      m.impacts.push(impacts[i]);
    }
  }

  private recycleOwned(m: YardRuntime): void {
    // When views share ArrayBuffer with transferred payload, recycling that
    // buffer for the next worker write is correct once we replace the views.
    // Chain/cannon ID buffers are recycled immediately in applySnapshot (not retained).
    const tBuf = m.transforms.buffer instanceof ArrayBuffer ? m.transforms.buffer : undefined;
    const sBuf =
      m.brickSleepStates.buffer instanceof ArrayBuffer ? m.brickSleepStates.buffer : undefined;
    const eBuf =
      m.brickEverAwake.buffer instanceof ArrayBuffer ? m.brickEverAwake.buffer : undefined;
    const rBuf = m.brickRetired.buffer instanceof ArrayBuffer ? m.brickRetired.buffer : undefined;
    const bBuf = m.brickBodyIds.buffer instanceof ArrayBuffer ? m.brickBodyIds.buffer : undefined;
    if (tBuf && tBuf.byteLength > 0) this.freeBuffers.transforms = tBuf;
    if (sBuf && sBuf.byteLength > 0) this.freeBuffers.brickSleepStates = sBuf;
    if (eBuf && eBuf.byteLength > 0) this.freeBuffers.brickEverAwake = eBuf;
    if (rBuf && rBuf.byteLength > 0) this.freeBuffers.brickRetired = rBuf;
    if (bBuf && bBuf.byteLength > 0) this.freeBuffers.brickBodyIds = bBuf;
  }

  private recycleBuffers(buffers: {
    transforms: ArrayBuffer;
    brickSleepStates: ArrayBuffer;
    brickEverAwake: ArrayBuffer;
    brickRetired: ArrayBuffer;
    brickBodyIds: ArrayBuffer;
    chainBodyIds: ArrayBuffer;
    cannonballBodyIds: ArrayBuffer;
  }): void {
    this.freeBuffers.transforms = buffers.transforms;
    this.freeBuffers.brickSleepStates = buffers.brickSleepStates;
    this.freeBuffers.brickEverAwake = buffers.brickEverAwake;
    this.freeBuffers.brickRetired = buffers.brickRetired;
    this.freeBuffers.brickBodyIds = buffers.brickBodyIds;
    this.freeBuffers.chainBodyIds = buffers.chainBodyIds;
    this.freeBuffers.cannonballBodyIds = buffers.cannonballBodyIds;
  }
}
